
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Award, BarChart, Target, TrendingUp } from 'lucide-react';
import { ExamStats } from '@/utils/exam/types';

interface ExamStatisticsProps {
  stats: ExamStats;
  loading?: boolean;
}

const ExamStatistics = ({ stats, loading = false }: ExamStatisticsProps) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-muted h-32 rounded-lg"></div>
        ))}
      </div>
    );
  }

  // Get stat values with fallbacks to handle missing data
  const { 
    averageScore = 0, 
    highestScore = 0, 
    totalExams = 0 
  } = stats || {};

  // Extract and transform exam types data
  const examTypeData = Object.entries(stats?.scoresByType || {})
    .filter(([_, data]) => data.count > 0)
    .map(([type, data]) => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      count: data.count,
      average: data.average.toFixed(1)
    }))
    .slice(0, 3); // Show only top 3 types

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Average Score Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Average Score</CardTitle>
          <BarChart className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{averageScore.toFixed(1)}%</div>
          <Progress value={averageScore} className="h-2 mt-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {totalExams} exams total
          </p>
        </CardContent>
      </Card>

      {/* Highest Score Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Highest Score</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{highestScore.toFixed(1)}%</div>
          <Progress value={highestScore} className="h-2 mt-2" />
          <p className="text-xs text-muted-foreground mt-1">Best performance</p>
        </CardContent>
      </Card>

      {/* Exams by Type Card */}
      <Card className="col-span-1 md:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Exams by Type</CardTitle>
          <Target className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {examTypeData.length > 0 ? (
            <div className="space-y-2">
              {examTypeData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Award className="h-4 w-4 mr-1 text-primary" />
                    <span className="text-sm">{item.type}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">{item.average}%</span>
                    <span className="text-xs text-muted-foreground">({item.count})</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-16 text-sm text-muted-foreground">
              No exam data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExamStatistics;
